import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getProfilePicture } from '@lf/api-client';
function Header({ childProfile, score, studyPlans, onChildProfile, onStudyPlan, onAddStudyPlan }) {
    const location = useLocation();
    const navigate = useNavigate();
    const [shouldAnimateBubbles, setShouldAnimateBubbles] = useState(false);
    const bubbleRefs = useRef([]);
    const wasLoggedInRef = useRef(Boolean(childProfile));

    useEffect(() => {
        const isLoggedIn = Boolean(childProfile);
        const wasLoggedIn = wasLoggedInRef.current;

        if (isLoggedIn && !wasLoggedIn) {
            setShouldAnimateBubbles(true);
        }
        if (!isLoggedIn) {
            setShouldAnimateBubbles(false);
        }

        wasLoggedInRef.current = isLoggedIn;
    }, [childProfile]);

    useEffect(() => {
        if (shouldAnimateBubbles) return;
        bubbleRefs.current.forEach((element) => {
            if (!element) return;
            element.style.opacity = '1';
            element.style.transform = 'translate3d(0, 0px, 0)';
        });
    }, [shouldAnimateBubbles]);

    function getPageType() {
        const values = location.pathname.split('/');
        return values[1];
    }

    function getStudyPlanId() {
        const values = location.pathname.split('/');
        return values[1] === 'feed' 
            ? new URLSearchParams(location.search).get('sp')
            : '';
    }

    function getStyle(studyPlanId) {
        return studyPlanId === getStudyPlanId() ? 'ring-blue-600' : 'ring-transparent';
    }

    function getHomeStyle() {
        return getPageType() === 'home' ? 'ring-blue-600' : 'ring-transparent';
    }

    const bubbleColors = [
        'bg-pastelblue/70',
        'bg-pastelyellow/70',
        'bg-pastelgreen/70',
        'bg-pastelpurple/70',
    ];

    const shouldShowStudyPlans = (Boolean(getStudyPlanId()) || getPageType() === 'home') && Boolean(childProfile);
    const shouldShowAddBubble = Boolean(studyPlans && studyPlans.length < 2 && onAddStudyPlan);

    const bubbleAnimationConfigs = useMemo(() => {
        if (!shouldShowStudyPlans) return [];

        const studyPlanCount = studyPlans?.length || 0;
        const bubbleCount = 1 + studyPlanCount + (shouldShowAddBubble ? 1 : 0);
        return Array.from({ length: bubbleCount }, () => {
            const delayMs = Math.floor(Math.random() * 1000);
            const durationMs = 3300 + Math.floor(Math.random() * 1001);
            const gravity = 1550 + Math.random() * 760;
            const restitution = 0.38 + Math.random() * 0.28;
            const bounceDamping = 0.88 + Math.random() * 0.1;
            const fallDistanceRatio = 0.7 + Math.random() * 0.26;

            return {
                delayMs,
                durationMs,
                gravity,
                restitution,
                bounceDamping,
                fallDistanceRatio,
            };
        });
    }, [shouldShowStudyPlans, shouldShowAddBubble, studyPlans]);

    useEffect(() => {
        bubbleRefs.current = bubbleRefs.current.slice(0, bubbleAnimationConfigs.length);
    }, [bubbleAnimationConfigs.length]);

    const canAnimateBubbles = shouldShowStudyPlans && bubbleAnimationConfigs.length > 0;

    useEffect(() => {
        if (!shouldAnimateBubbles || !canAnimateBubbles) return;
        const timer = setTimeout(() => setShouldAnimateBubbles(false), 6200);
        return () => clearTimeout(timer);
    }, [shouldAnimateBubbles, canAnimateBubbles]);

    useEffect(() => {
        if (!shouldAnimateBubbles || !canAnimateBubbles) return;

        let animationFrameId;
        const startTime = performance.now();
        const viewportHeight = typeof window !== 'undefined' ? window.innerHeight : 900;

        const simulations = bubbleAnimationConfigs
            .map((config, index) => {
                const element = bubbleRefs.current[index];
                if (!element) return null;

                const sim = {
                    element,
                    delayMs: config.delayMs,
                    durationMs: config.durationMs,
                    gravity: config.gravity,
                    restitution: config.restitution,
                    bounceDamping: config.bounceDamping,
                    y: -Math.max(280, Math.round(viewportHeight * config.fallDistanceRatio)),
                    vy: 0,
                    bounceCount: 0,
                    lastTime: null,
                    done: false,
                };

                sim.element.style.opacity = '0';
                sim.element.style.transform = `translate3d(0, ${sim.y}px, 0)`;
                return sim;
            })
            .filter(Boolean);

        if (!simulations.length) return;

        const tick = (now) => {
            let allDone = true;

            simulations.forEach((sim) => {
                if (sim.done) return;
                allDone = false;

                const elapsed = now - startTime;
                if (elapsed < sim.delayMs) return;

                const localElapsed = elapsed - sim.delayMs;
                if (localElapsed >= sim.durationMs) {
                    sim.done = true;
                    sim.element.style.opacity = '1';
                    sim.element.style.transform = 'translate3d(0, 0px, 0)';
                    return;
                }

                const rawDt = sim.lastTime ? Math.min((now - sim.lastTime) / 1000, 0.04) : 0;
                sim.lastTime = now;

                if (rawDt > 0) {
                    const substep = 1 / 120;
                    const steps = Math.max(1, Math.ceil(rawDt / substep));
                    const dt = rawDt / steps;

                    for (let i = 0; i < steps; i += 1) {
                        sim.vy += sim.gravity * dt;
                        sim.y += sim.vy * dt;

                        if (sim.y > 0) {
                            sim.y = 0;
                            sim.vy = -Math.abs(sim.vy) * sim.restitution * Math.pow(sim.bounceDamping, sim.bounceCount);
                            sim.bounceCount += 1;
                        }
                    }
                }

                if (localElapsed > sim.durationMs * 0.62 && Math.abs(sim.vy) < 22 && Math.abs(sim.y) < 0.9) {
                    sim.done = true;
                    sim.element.style.opacity = '1';
                    sim.element.style.transform = 'translate3d(0, 0px, 0)';
                    return;
                }

                const fadeProgress = Math.min(1, localElapsed / 180);
                sim.element.style.opacity = fadeProgress.toFixed(3);
                sim.element.style.transform = `translate3d(0, ${Math.min(0, sim.y).toFixed(2)}px, 0)`;
            });

            if (!allDone) {
                animationFrameId = requestAnimationFrame(tick);
            }
        };

        animationFrameId = requestAnimationFrame(tick);
        return () => {
            if (animationFrameId) {
                cancelAnimationFrame(animationFrameId);
            }
            simulations.forEach((sim) => {
                sim.element.style.opacity = '1';
                sim.element.style.transform = 'translate3d(0, 0px, 0)';
            });
        };
    }, [shouldAnimateBubbles, canAnimateBubbles, bubbleAnimationConfigs]);

    const handleHomeClick = () => {
        navigate('/home');
    };

    return <nav className="bg-sky pt-4">
        <div className={`flex justify-between mx-auto max-w-screen-lg ${getPageType() === "video" ? "mb-4" : "mb-2"}`}>
            {/* Left-aligned content */}
            <div>
            </div>
        
            {/* Right-aligned div */}
            <div className="ml-auto flex flex-col">
                <div className="mr-4" onClick={onChildProfile}>
                    {
                    score > 0 && <span className="bg-yellow-300 rounded-2xl border p-2">
                        ⭐️ {score}
                    </span>
                    }
                    { 
                    childProfile 
                        ? <span className="cursor-pointer bg-white rounded-2xl border border-white p-2 ml-1">
                            {childProfile.first_name + ' ' + getProfilePicture(childProfile, '🐠')}
                        </span>
                        : '' 
                    }
                </div>
            </div>
        </div>

        {
            getPageType() !== 'video' && <div className="flex justify-between">
                {/* Left-aligned content */}
                <div className="w-1/3 md:w-1/4 2xl:w-1/5 flex items-end">
                    <img src="/bg-sky-top-left.jpeg" alt="" className="w-full h-full" />
                </div>

                {/* Center-aligned content */}
                <div className="z-10 w-1/3 md:w-1/2 2xl:w-3/5 flex items-center justify-center">
                {
                    shouldShowStudyPlans && (
                        <div className="flex justify-center gap-4">
                            <div className="text-center">
                                <div
                                    ref={(element) => { bubbleRefs.current[0] = element; }}
                                    className={`w-24 h-24 flex items-center justify-center mx-auto cursor-pointer ${shouldAnimateBubbles ? 'bubble-physics-enter' : ''}`}
                                    onClick={handleHomeClick}
                                >
                                    <div className={`w-[88px] h-[88px] rounded-full ${getPageType() === 'home' ? bubbleColors[0] : 'bg-white'} ring-[1.5px] ring-offset-[6px] ring-offset-white ${getHomeStyle()} flex items-center justify-center`}>
                                        🏠
                                    </div>
                                </div>
                                <div className="mt-2">Home</div>
                            </div>
                            {
                            studyPlans && studyPlans.map((studyPlan, index) => (
                                <div key={studyPlan.id} className="text-center">
                                    <div
                                            ref={(element) => { bubbleRefs.current[index + 1] = element; }}
                                            className={`w-24 h-24 flex items-center justify-center mx-auto cursor-pointer ${shouldAnimateBubbles ? 'bubble-physics-enter' : ''}`}
                                            onClick={() => onStudyPlan(studyPlan.id)}>
                                        <div className={`w-[88px] h-[88px] rounded-full ${studyPlan.id === getStudyPlanId() ? bubbleColors[(index + 1) % bubbleColors.length] : 'bg-white'} ring-[1.5px] ring-offset-[6px] ring-offset-white ${getStyle(studyPlan.id)} flex items-center justify-center`}>
                                            { studyPlan.emoji || '🐠' }
                                        </div>
                                    </div>
                                    <div className="mt-2">
                                        {studyPlan.subject}
                                    </div>
                                </div>
                            ))
                            }
                            {shouldShowAddBubble && (
                                <div className="text-center cursor-pointer" onClick={onAddStudyPlan}>
                                    <div
                                        ref={(element) => { bubbleRefs.current[(studyPlans?.length || 0) + 1] = element; }}
                                        className={`w-24 h-24 flex items-center justify-center mx-auto ${shouldAnimateBubbles ? 'bubble-physics-enter' : ''}`}>
                                        <div className={`w-[88px] h-[88px] rounded-full bg-white ring-[1.5px] ring-transparent ring-offset-[6px] ring-offset-white flex items-center justify-center`}>
                                            ＋
                                        </div>
                                    </div>
                                    <div className="mt-2">
                                        Add study plan
                                    </div>
                                </div>
                            )}
                        </div>
                    )
                }
                </div>

                {/* Right-aligned div */}
                <div className="ml-auto w-1/3 md:w-1/4 2xl:w-1/5 flex items-end">
                    <img src="/bg-sky-top-right.jpeg" alt="" className="w-full h-full" />
                </div>
            </div>
        }
        
    </nav>;    
}

export default Header;
